// Bootstrap: wires net/input/state/render/ui together and runs the render loop.
//
// Controls — tap to move only (predicted locally; server is authoritative for
// combat/HP). There is NO hold-to-move and NO drag-steer: each tap is one
// discrete command.
//   - TAP the ground: walk there.
//   - TAP a monster: walk into range and auto-fire fireballs until it dies.
//   - TAP a player: nothing — you never walk onto another player.
//   - TAP a gold pile: walk onto it and pick it up.
// SELF predicts movement immediately; OTHER entities are dead-reckoned (state.ts).

import "./style.css";

import {
  PLAYER_SPEED,
  PLAYER_RADIUS,
  MONSTER_RADIUS,
  WORLD_WIDTH,
  WORLD_HEIGHT,
  DEFAULT_NAME,
  PLAYER_MAX_HP,
  ATTACK_RANGE,
  FIREBALL_SPEED,
} from "@mmo/shared";

import * as storage from "./storage";
import { Net } from "./net";
import { Input } from "./input";
import { WorldState } from "./state";
import { Renderer } from "./render";
import type { Projectile } from "./render";
import { UI } from "./ui";

// --- Module state -----------------------------------------------------------
const canvas = document.getElementById("game") as HTMLCanvasElement | null;
if (!canvas) throw new Error("missing #game canvas");

const world = new WorldState();
const renderer = new Renderer(canvas);

// Predicted self position + current move destination (world space).
let selfX = 0;
let selfY = 0;
let selfTX = 0;
let selfTY = 0;
let selfMoving = false;
let predictionSeeded = false;

// Monotonic command sequence number; throttle bookkeeping for move commands.
let inputSeq = 0;
let pendingMove = false;
let lastMoveSentAt = 0;
const MOVE_SEND_INTERVAL = 50; // ms: cap outbound move commands at ~20/s

// Active visual fireballs.
const projectiles: Projectile[] = [];

// --- Net --------------------------------------------------------------------
const initialName = storage.getName() ?? DEFAULT_NAME;
let ui: UI;

const net = new Net(storage.getToken(), storage.getName(), {
  onStatusChange: (status) => ui.setStatus(status),

  onWelcome: (msg) => {
    storage.setToken(msg.token);
    storage.setName(msg.name);
    world.setSelf(msg.self);
    world.setWorld(msg.world.w, msg.world.h);
    ui.setName(msg.name);
    net.setIdentity(msg.token, msg.name);
    // Clear any state from a previous session so reconnects don't leave ghost
    // entities (old netIds) duplicated alongside the fresh enter dump.
    world.reset();
    projectiles.length = 0;
    predictionSeeded = false;
    selfMoving = false;
    pendingMove = false;
    clearTargets();
    // Reset the HUD to a fresh game: every login spawns at the origin with 0
    // gold (position/gold are not persisted), so clear any leftover total from
    // a previous session rather than showing a stale number until the next
    // gold event.
    ui.setGold(0);
    ui.setPing(null);
    // Keep the loading gate up until the first snapshot positions us, so the
    // init (empty origin view -> snap to player) isn't visible.
    ui.setReady(false);
  },

  onEnter: (entities) => {
    world.addEntities(entities);
    ui.setCount(world.playerCount());
    if (!predictionSeeded) {
      const me = entities.find((p) => p.id === world.selfId);
      if (me) {
        selfX = me.x;
        selfY = me.y;
        selfTX = me.x;
        selfTY = me.y;
        predictionSeeded = true;
        ui.setReady(true); // world loaded + self positioned -> reveal the game
      }
    }
    const me = world.self();
    if (me) ui.setHp(me.hp, me.maxHp);
  },

  onPath: (ev) => {
    if (ev.id !== world.selfId) {
      world.applyPath(ev.id, ev.tx, ev.ty, ev.moving);
      return;
    }
    // A self path just echoes our own prediction — movement is deterministic,
    // so we ignore it (no reconciliation). Forced moves arrive as `teleport`.
  },

  onLeave: (id) => {
    world.leave(id);
    ui.setCount(world.playerCount());
    if (id === attackId) attackId = null;
  },

  onHp: (id, hp) => {
    world.setHp(id, hp);
    if (id === world.selfId) ui.setHp(hp, world.self()?.maxHp ?? PLAYER_MAX_HP);
  },

  onRename: (id, name) => {
    world.setEntityName(id, name);
    if (id === world.selfId) ui.setName(name);
  },

  onFireball: (ev) => {
    projectiles.push({ x: ev.fromX, y: ev.fromY, targetId: ev.targetId });
  },

  onTeleport: (id, x, y) => {
    world.teleport(id, x, y);
    if (id === world.selfId) {
      // Respawn: snap prediction and drop any in-flight intents.
      selfX = x;
      selfY = y;
      selfTX = x;
      selfTY = y;
      selfMoving = false;
      pendingMove = false;
      clearTargets();
    }
  },

  onMoneySpawn: (money) => world.addMoney(money),
  onMoneyGone: (id) => {
    world.removeMoney(id);
    if (id === pickupId) pickupId = null;
  },
  onGold: (amount) => ui.setGold(amount),

  onPong: (rtt) => ui.setPing(rtt),
  onEmailSent: (purpose) => ui.onEmailSent(purpose),
  onEmailBound: (email) => ui.onEmailBound(email),
  onSignedIn: (token) => ui.onSignedIn(token),
  onError: (code, message) => ui.onError(code, message),
});

ui = new UI(net);
ui.init();
ui.setName(initialName);
ui.setStatus("connecting");

// --- World bounds + tuning --------------------------------------------------
const MIN_X = -WORLD_WIDTH / 2 + PLAYER_RADIUS;
const MAX_X = WORLD_WIDTH / 2 - PLAYER_RADIUS;
const MIN_Y = -WORLD_HEIGHT / 2 + PLAYER_RADIUS;
const MAX_Y = WORLD_HEIGHT / 2 - PLAYER_RADIUS;

const MOVE_EPSILON = 1;
// Chase to INSIDE attack range, not its edge: prediction/latency jitter at the
// exact boundary otherwise leaves the server seeing us a hair out of range, so
// we'd stop and never fire. The buffer guarantees we're comfortably in range.
const ATTACK_STOP_DIST = ATTACK_RANGE * 0.85;
// Collect gold only once we're essentially on top of it (under the body), so it
// isn't grabbed from a distance. The server still allows up to MONEY_PICKUP_RANGE.
const PICKUP_REACH = PLAYER_RADIUS;
const PICK_SLOP = 10;

// --- Command/input state ----------------------------------------------------
let attackId: number | null = null; // monster being attacked (persists)
let pickupId: number | null = null; // money pile we'll grab when we reach it
let lastPickupAt = 0; // throttle pickup commands while walking onto gold

/** Clear the client-only pickup intent (no server state). */
function clearTransient(): void {
  pickupId = null;
}

/** Full local reset (respawn / reconnect); the server has already cleared too. */
function clearTargets(): void {
  attackId = null;
  pickupId = null;
}

/** Cancel a server-side auto-attack, if any. */
function clearAttack(): void {
  if (attackId !== null) {
    net.send({ t: "attack", seq: ++inputSeq, targetId: 0 });
    attackId = null;
  }
}

/** Start a fresh action: cancel any attack, drop pickup intents. */
function cancelActions(): void {
  clearAttack();
  clearTransient();
}

/** Nearest other entity whose body is under a world point, else null. */
function pickEntity(wx: number, wy: number): { id: number; kind: string } | null {
  let best: { id: number; kind: string } | null = null;
  let bestD2 = Infinity;
  for (const e of world.entities.values()) {
    if (e.id === world.selfId) continue;
    const r = (e.kind === "monster" ? MONSTER_RADIUS : PLAYER_RADIUS) + PICK_SLOP;
    const dx = e.renderX - wx;
    const dy = e.renderY - wy;
    const d2 = dx * dx + dy * dy;
    if (d2 <= r * r && d2 < bestD2) {
      bestD2 = d2;
      best = { id: e.id, kind: e.kind };
    }
  }
  return best;
}

/** Nearest money pile under a world point, else null. */
function pickMoney(wx: number, wy: number): number | null {
  const r = 14 + PICK_SLOP;
  let best: number | null = null;
  let bestD2 = r * r;
  for (const m of world.money.values()) {
    const dx = m.x - wx;
    const dy = m.y - wy;
    const d2 = dx * dx + dy * dy;
    if (d2 <= bestD2) {
      bestD2 = d2;
      best = m.id;
    }
  }
  return best;
}


/** Set the predicted destination, emitting a move only when it changes. */
function aimAt(wx: number, wy: number): void {
  const ntx = clamp(wx, MIN_X, MAX_X);
  const nty = clamp(wy, MIN_Y, MAX_Y);
  const moving = Math.hypot(ntx - selfX, nty - selfY) > MOVE_EPSILON;
  if (moving || selfMoving) pendingMove = true;
  selfTX = ntx;
  selfTY = nty;
  selfMoving = moving;
}

// --- Input ------------------------------------------------------------------
// One tap = one command. No hold-to-move, no drag-steer.
const input = new Input(canvas, (cx, cy) => {
  if (!predictionSeeded) return;
  const wp = renderer.screenToWorld(cx, cy);
  const ent = pickEntity(wp.x, wp.y);

  // Tap a monster -> attack it (walk into range + auto-fire). Overwrites any
  // prior attack on the server.
  if (ent && ent.kind === "monster") {
    clearTransient();
    attackId = ent.id;
    net.send({ t: "attack", seq: ++inputSeq, targetId: ent.id });
    return;
  }
  // Tap a player -> nothing; we never walk onto another player.
  if (ent && ent.kind === "player") return;

  // Tap the ground (or a gold pile) -> walk to exactly where you tapped. Gold
  // under the tap arms a pickup side-effect; movement still goes to the point.
  cancelActions();
  const money = pickMoney(wp.x, wp.y);
  if (money !== null) {
    pickupId = money;
    lastPickupAt = 0;
  }
  aimAt(wp.x, wp.y);
});
input.attach();

// Block mobile pinch / double-tap zoom (iOS ignores user-scalable=no).
document.addEventListener("gesturestart", (e) => e.preventDefault());
let lastTouchEnd = 0;
document.addEventListener(
  "touchend",
  (e) => {
    const now = performance.now();
    if (now - lastTouchEnd < 300) e.preventDefault();
    lastTouchEnd = now;
  },
  { passive: false },
);

// We do NOT auto-connect: the login gate's "Continue as guest" button starts
// the connection (ui.ts). Reconnects after a drop are still automatic.

let lastT = performance.now();

/** Walk the predicted self position toward its destination this frame. */
function marchSelf(dt: number): void {
  if (!selfMoving) return;
  const dx = selfTX - selfX;
  const dy = selfTY - selfY;
  const dist = Math.hypot(dx, dy);
  const stepLen = PLAYER_SPEED * dt;
  if (dist <= stepLen || dist === 0) {
    selfX = selfTX;
    selfY = selfTY;
    selfMoving = false;
  } else {
    selfX = clamp(selfX + (dx / dist) * stepLen, MIN_X, MAX_X);
    selfY = clamp(selfY + (dy / dist) * stepLen, MIN_Y, MAX_Y);
  }
}

/** Move toward an entity, stopping `stopDist` short of it. */
function chaseTo(rx: number, ry: number, stopDist: number): void {
  const dx = rx - selfX;
  const dy = ry - selfY;
  const dist = Math.hypot(dx, dy);
  if (dist > stopDist) {
    const k = (dist - stopDist) / dist;
    aimAt(selfX + dx * k, selfY + dy * k);
  } else {
    aimAt(selfX, selfY);
  }
}

/** Decide where the player is headed this frame from input / active target. */
function updateDestination(): void {
  // Pickup is a pure SIDE-EFFECT: grab pending gold when we're on top of it. It
  // does NOT steer — movement always follows the waypoint you clicked, so we
  // keep heading there whether or not the gold gets picked up.
  if (pickupId !== null) {
    const m = world.money.get(pickupId);
    if (!m) {
      pickupId = null;
    } else if (
      Math.hypot(m.x - selfX, m.y - selfY) <= PICKUP_REACH &&
      performance.now() - lastPickupAt > 250
    ) {
      net.send({ t: "pickup", moneyId: pickupId });
      lastPickupAt = performance.now();
    }
  }

  // Attack is the only auto-driver: chase the tapped monster into range
  // (pursuit of the target, not manual steering). With no attack target, the
  // tapped waypoint stands and marchSelf walks to it.
  if (attackId !== null) {
    const e = world.entities.get(attackId);
    if (!e || e.kind !== "monster") attackId = null;
    else chaseTo(e.renderX, e.renderY, ATTACK_STOP_DIST);
  }
}

/** Advance fireball visuals toward their targets; drop on arrival / target loss. */
function updateProjectiles(dt: number): void {
  const step = FIREBALL_SPEED * dt;
  for (let i = projectiles.length - 1; i >= 0; i--) {
    const p = projectiles[i]!;
    const tgt = world.entities.get(p.targetId);
    if (!tgt) {
      projectiles.splice(i, 1);
      continue;
    }
    const dx = tgt.renderX - p.x;
    const dy = tgt.renderY - p.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= step) {
      projectiles.splice(i, 1); // hit
    } else {
      p.x += (dx / dist) * step;
      p.y += (dy / dist) * step;
    }
  }
}

function frame(now: number): void {
  let dt = (now - lastT) / 1000;
  lastT = now;
  if (dt > 0.1) dt = 0.1;

  if (predictionSeeded) {
    updateDestination();
    marchSelf(dt);
    if (pendingMove && now - lastMoveSentAt >= MOVE_SEND_INTERVAL) {
      net.send({ t: "move", seq: ++inputSeq, x: selfTX, y: selfTY });
      lastMoveSentAt = now;
      pendingMove = false;
    }
  }

  world.step(dt);
  updateProjectiles(dt);

  // Reticle on the attack target; destination marker only when freely moving.
  const target = attackId !== null ? world.entities.get(attackId) : undefined;
  const ringAt = target ? { x: target.renderX, y: target.renderY } : null;
  const showMarker = selfMoving && attackId === null;

  renderer.draw(
    world,
    selfX,
    selfY,
    showMarker ? { x: selfTX, y: selfTY } : null,
    ringAt,
    projectiles,
  );

  requestAnimationFrame(frame);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

requestAnimationFrame(frame);
